import type { BillingIntervals, BillingProcess, LimitNames, PlanLimitsType, PlanTypes } from '@gonfalon/billing/types';
import type { schemas } from '@gonfalon/openapi/types';

import { type CatalogPlanId } from '../internal/plan/planData/planIds';

// Catalog plans are keyed by the server's `planKey`; the stored selection also records which source produced it.
export type PlanSource = 'legacy' | 'catalog';
export type PlanId = CatalogPlanId | (string & {});

export type Invoice = {
  key: string;
  date: string;
  _links?: {
    doc?: {
      href?: string;
    };
  };
};

export type Plan = {
  id: PlanId;
  planType: PlanTypes;
  billingProcess: BillingProcess;
  checkoutProcess: CheckoutProcess; // Next step in the checkout process
  marketingCopy: MarketingCopy; // Unique copy related to a specific plan
  versions: Record<string, Version>; // Plan versioning
  recommended?: boolean; // Whether a plan is emphasized in checkout
  disableIf?: Array<{ planType: PlanTypes; version?: number }>; // Disable button when subscription matches. Omit version to match any version of that plan.
  hasConfigurableLimits?: boolean; // Whether the plan has configurable limits (defaults to false for fixed plans)
  isFoundationPlan?: boolean; // Whether the plan is a Foundation-tier plan (shows payment card UI)
  isFreeDevPlan?: boolean; // Whether the plan is a free developer plan (hides payment card requirement)
  isDeveloperSignup?: boolean; // Whether this plan counts as a developer signup (for analytics tracking)
  isLegacyDeveloperFacade?: boolean; // Whether this is the legacy Developer plan facade (displays as "Developer / Foundation")
  useObservabilityFlagForVersion?: boolean; // Whether to check enableSelfServeObservability flag for version selection (defaults to false, meaning always use version 1)
  isCurrent?: boolean; // The account's current plan; legacy plans derive this from the subscription instead
  isSelectable?: boolean; // false disables the CTA; legacy plans use disableIf instead
  requiresPaymentMethod?: boolean; // Checkout must collect a card
  requiresTermsAcceptance?: boolean; // Show the subscription-terms checkbox
  requiresOverageAuthorization?: boolean; // Show the overage-authorization checkbox
  hasAutomaticRenewal?: boolean; // Terms copy mentions automatic renewal
  marketingCopyByBundle?: Record<string, MarketingCopy>; // Catalog copy per bundle; `marketingCopy` is the default
  hasBillableProducts?: boolean; // Some product is charged past its included quantity; drives overage authorization
  version?: number; // The real plan version sent on requests; legacy plans resolve theirs through getPlanVersion
  selectableBillingProcesses?: BillingProcess[]; // Billing processes reachable from the account's current state
  defaultBillingInterval?: BillingIntervals; // Interval preselected once this plan is chosen; wins over the account-wide default
  catalog?: schemas['Plan']; // The wire plan for catalog-only facts (attributes); consumers should read the derived fields above
};

export type BulkPricingTier = {
  min: number;
  max: number;
  pricePerBlockCents: number;
};

// One priced band of a catalog ladder, as the contract serves it: inclusive bounds, `max` null when unbounded,
// and `basePriceCents` the absolute price of selecting exactly `min`.
export type CatalogPriceTier = {
  min: number;
  max: number | null;
  basePriceCents: number;
  pricePerBlockCents: number;
};

export type CatalogPriceLadder = {
  blockSize: number;
  tiers: CatalogPriceTier[];
};

export type AnnualCommitOverageProductDetails = {
  hasBulkPricing?: boolean;
  isUnbilled?: boolean;
  limitName: LimitNames;
  freeQuantity: number;
  annualCommitDetails: {
    blockSize: number;
    pricePerBlockCents: number;
    maxQuantity: number;
    bulkPricingTiers?: BulkPricingTier[];
  };
  annualPriceLadder?: CatalogPriceLadder; // Catalog plans price from this ladder instead of the bulk-pricing math
  monthlyOverageDetails: {
    blockSize: number;
    pricePerBlockCents: number;
  };
  unitName?: string;
  stepSize?: number; // Stepper increment from the catalog; defaults to the block size when absent
  displayOnly?: boolean; // Shown as inclusion but not billed (e.g. AI runs pre-billing)
  monthlyBilledOnly?: boolean; // Billed monthly but not annually; shows as disabled in annual tables (e.g. AI runs)
  gatedByFlag?: () => boolean; // See `getVisiblePlanProducts`
  serviceConnectionDiscount?: { rate: number };
};

export type ProductVisibilityContext =
  | { source: 'session' }
  | { source: 'subscription'; subscriptionLimits: PlanLimitsType };

type AnnualCommitOverageVersion = {
  planTitle?: string;
  hasAnnualBilling?: boolean; // Whether this version supports annual billing (defaults to true)
  products: AnnualCommitOverageProductDetails[];
  marketingCopy?: Partial<MarketingCopy>; // Per-version marketing copy overrides merged on top of `Plan.marketingCopy`
  disableIf?: Array<{ planType: PlanTypes; version?: number }>; // Disable button when subscription matches. Omit version to match any version of that plan.
};

export type Version = AnnualCommitOverageVersion;

export type ProductDetails = AnnualCommitOverageProductDetails;

export type ResolvedVersion = Version & { versionKey: number | undefined };

export type MarketingCopy = {
  name: string; // e.g. 'Foundation'
  tagLine: string; // e.g. 'A feature-rich starter plan with essential tools for builders'
  pricingHeadline: string; // e.g. 'Monthly pay-as-you-go'
  pricingDescription: string; // e.g. 'Pay-as-you-go only when you exceed your monthly allowance'
  featuresHeader: string; // Header for feature list (e.g. 'Everything in Developer, plus')
  features: Feature[]; // Default feature list
  featureGroups?: FeatureGroup[]; // Grouped feature sections (e.g. CodeControl + AgentControl). Overrides features/featuresHeader when present.
  annualDiscount?: string; // e.g. '2 free months' - marketing copy for annual plan discount
  ctaButtonText?: string; // Custom CTA button text for plan selection (e.g. 'Select plan')
  checkoutCtaText?: string; // Custom CTA button text for checkout confirmation (e.g. 'Get started', 'Confirm subscription')
  monthlyPriceText?: string; // Custom monthly price display text (e.g. 'FREE', 'Usage-based')
  calculatorLink?: CalculatorLink;
  afterTrialTag?: AfterTrialTag; // Tag shown during trial to indicate this plan will be active after trial ends
};

export type CalculatorLink = {
  label: string;
  url: string;
};

export type AfterTrialTag = {
  text: string; // Tag text (e.g. "Plan after trial")
  tooltip: string; // Tooltip text explaining what happens after trial
};

export enum CheckoutProcess {
  DIRECT_CHECKOUT = 'directCheckout', // Fixed limits are provided for easy checkout
  CUSTOMIZE_COMMITS = 'customizeCommits', // Plan limits are customizable in checkout
  CONTACT_US = 'contactUs', // Changing a plan requires contacting us
}

export type Feature = {
  text: string;
  tooltip?: {
    text: string;
    ctaTarget?: string; // This should be a valid URL or a valid path in the app
    ctaText?: string; // Button label; defaults to 'Learn more' for links, 'Contact us' for modal actions
    ctaAction?: 'contact_sales'; // Opens the ContactUs modal instead of navigating to ctaTarget
  };
  gatedByFlag?: () => boolean; // Evaluated at render time; feature is hidden when this returns false.
};

export type FeatureGroup = {
  header: string; // Section header (e.g. 'CodeControl', 'AgentControl')
  features: Feature[];
};

export type CtaButtonProps = {
  plan: Plan;
  variant?: 'default' | 'primary';
  versionKey?: number;
};

export enum OverageInfoModalMode {
  MONTHLY = 'MONTHLY',
  AUTOMATIC = 'AUTOMATIC',
}
